import { observable, configure, action } from 'mobx';
import LocalModel from './localModel';
import util from 'preact-util';
import { v4 as uuidv4 } from 'uuid';

configure({ enforceActions: 'always' });

class LotTestingStore extends LocalModel {
    constructor() {
        super('lotTesting', {
            namePlural: 'lotTestings',
            namePluralReal: 'lottestings',
            sort: '-createdDate',
            limit: 100,
            api: {
                search: {
                    url: '/api/lottestings/',
                    params: {
                        limit: 100,
                        sort: '-createdDate',
                    },
                },
                load: {
                    url: '/api/lottestings/',
                    params: {},
                },
                save: {
                    url: '/api/lottestings/',
                    params: {},
                },
                delete: {
                    url: '/api/lottestings/',
                    params: {},
                },
            },
        });
    }

    @observable total = 0;

    @observable limit = 100;

    @observable offset = 0;

    @observable currentPage = 1;

    @observable newLotTesting = {};

    @observable lotTesting = {};

    @observable lotTestings = [];

    @observable searchResults = [];

    @observable isExtracting = false;

    /**
     * Load a single LOT testing entry with comments
     * @param {number} id - The ID of the LOT testing entry
     */
    @action
    async loadWithComments(id) {
        const response = await util.fetchApi(`/api/lottestings/${id}`, { method: 'GET' }, { addData: 'comments' });
        if (response?.status === 200 && response.data?.length > 0) {
            this.updateKeyValue('lotTesting', response.data[0]);
        }
        return response;
    }

    /**
     * Add a new weapon test to a LOT testing entry
     * @param {number} lotTestingId - The ID of the LOT testing entry
     * @param {object} weaponTest - The weapon test data
     */
    @action
    async addWeaponTest(lotTestingId, weaponTest) {
        // Add uuidv4 to the weapon test for unique identification
        const testWithUuid = {
            ...weaponTest,
            uuidv4: uuidv4(),
        };

        const response = await util.fetchApi(`/api/lottestings/${lotTestingId}`, { method: 'PATCH' }, {
            addWeaponTest: testWithUuid,
        });

        if (response?.status === 202) {
            // Reload the LOT testing to get updated data
            await this.load(lotTestingId);
        }

        return response;
    }

    /**
     * Remove a weapon test from a LOT testing entry
     * @param {number} lotTestingId - The ID of the LOT testing entry
     * @param {string} testUuidv4 - The uuidv4 of the weapon test to remove
     */
    @action
    async removeWeaponTest(lotTestingId, testUuidv4) {
        const response = await util.fetchApi(`/api/lottestings/${lotTestingId}`, { method: 'PATCH' }, {
            removeWeaponTestsByUuidv4: testUuidv4,
        });

        if (response?.status === 202) {
            // Reload the LOT testing to get updated data
            await this.load(lotTestingId);
        }

        return response;
    }

    /**
     * Edit a weapon test in a LOT testing entry
     * @param {number} lotTestingId - The ID of the LOT testing entry
     * @param {string} testUuidv4 - The uuidv4 of the weapon test to edit
     * @param {string} key - The field key to edit
     * @param {any} value - The new value
     */
    @action
    async editWeaponTest(lotTestingId, testUuidv4, key, value) {
        const response = await util.fetchApi(`/api/lottestings/${lotTestingId}`, { method: 'PATCH' }, {
            editWeaponTestsByUuidv4: testUuidv4,
            editWeaponTestsKey: key,
            editWeaponTestsValue: value,
        });

        if (response?.status === 202) {
            // Reload the LOT testing to get updated data
            await this.load(lotTestingId);
        }

        return response;
    }

    /**
     * Update weapon tests array with weather data auto-fetch
     * @param {number} lotTestingId - The ID of the LOT testing entry
     * @param {array} weaponTests - The complete weapon tests array
     */
    @action
    async updateWeaponTests(lotTestingId, weaponTests) {
        // Ensure all tests have uuidv4
        const testsWithUuids = weaponTests.map(test => ({
            ...test,
            uuidv4: test.uuidv4 || uuidv4(),
        }));

        const response = await util.fetchApi(`/api/lottestings/${lotTestingId}`, { method: 'PATCH' }, {
            weaponTests: testsWithUuids,
        });

        if (response?.status === 202) {
            // Reload the LOT testing to get updated data
            await this.load(lotTestingId);
        }

        return response;
    }

    /**
     * Add a new weapon test to the local newLotTesting object (before saving)
     * @param {object} weaponTest - The weapon test data
     */
    @action
    addWeaponTestToNew(weaponTest) {
        const testWithUuid = {
            ...weaponTest,
            uuidv4: uuidv4(),
        };

        const currentTests = this.newLotTesting.weaponTests || [];
        this.updateObjectKeyValue('newLotTesting', 'weaponTests', [...currentTests, testWithUuid]);
    }

    /**
     * Remove a weapon test from the local newLotTesting object (before saving)
     * @param {string} testUuidv4 - The uuidv4 of the weapon test to remove
     */
    @action
    removeWeaponTestFromNew(testUuidv4) {
        const currentTests = this.newLotTesting.weaponTests || [];
        const updatedTests = currentTests.filter(test => test.uuidv4 !== testUuidv4);
        this.updateObjectKeyValue('newLotTesting', 'weaponTests', updatedTests);
    }

    /**
     * Add a weapon test to the current lotTesting being edited (for view component)
     * @param {object} weaponTest - The weapon test data
     */
    @action
    addWeaponTestToEdit(weaponTest) {
        const testWithUuid = {
            ...weaponTest,
            uuidv4: uuidv4(),
        };

        const currentTests = this.lotTesting.weaponTests || [];
        this.updateObjectKeyValue('lotTesting', 'weaponTests', [...currentTests, testWithUuid]);
    }

    /**
     * Remove a weapon test from the current lotTesting being edited (for view component)
     * @param {string} testUuidv4 - The uuidv4 of the weapon test to remove
     */
    @action
    removeWeaponTestFromEdit(testUuidv4) {
        const currentTests = this.lotTesting.weaponTests || [];
        const updatedTests = currentTests.filter(test => test.uuidv4 !== testUuidv4);
        this.updateObjectKeyValue('lotTesting', 'weaponTests', updatedTests);
    }

    /**
     * Call backend to extract LOT fields and weapon tests from uploaded images.
     * @param {string[]} imageKeys - S3 keys from images[].newFilename (max 3)
     * @param {number|null} lotTestingId - Existing LOT id (for logging), or null for new entries
     */
    @action
    async extractFromImages(imageKeys, lotTestingId) {
        this.updateKeyValue('isExtracting', true);
        try {
            const body = { imageKeys };
            if (lotTestingId) body.id = lotTestingId;
            const response = await util.fetchApi('/api/lottestings/extract-from-image', { method: 'POST' }, body);
            if (response?.status === 200) {
                return response.data || null;
            }
            return null;
        } finally {
            this.updateKeyValue('isExtracting', false);
        }
    }

    /**
     * Merge extracted LOT fields into the local object (new or existing).
     * Only fills fields that are currently empty/zero - preserves user edits.
     * For existing entries, also PATCHes the filled fields to the server.
     * @param {object} lotFields - Extracted LOT-level fields
     * @param {boolean} isNew - Whether editing newLotTesting (true) or lotTesting (false)
     * @param {number|null} lotTestingId - Existing LOT id, or null for new entries
     * @returns {number} count of fields that were actually filled
     */
    async mergeLotFields(lotFields, isNew, lotTestingId) {
        const target = isNew ? this.newLotTesting : this.lotTesting;
        const merged = { ...target };
        const filled = {};
        Object.keys(lotFields).forEach(key => {
            const incoming = lotFields[key];
            const current = merged[key];
            const currentEmpty = current === undefined || current === null || current === '' || current === 0;
            const incomingValid = incoming !== undefined && incoming !== null && incoming !== '';
            if (currentEmpty && incomingValid) {
                merged[key] = incoming;
                filled[key] = incoming;
            }
        });
        this.updateKeyValue(isNew ? 'newLotTesting' : 'lotTesting', merged);

        if (!isNew && lotTestingId && Object.keys(filled).length > 0) {
            await util.fetchApi(`/api/lottestings/${lotTestingId}`, { method: 'PATCH' }, filled);
        }

        return Object.keys(filled).length;
    }

    /**
     * Append extracted weapon tests to the LOT testing entry.
     * For new entries: adds to local newLotTesting state via addWeaponTestToNew.
     * For existing entries: PATCHes each test to the server via addWeaponTest.
     * Both existing methods already generate uuidv4 internally.
     * @param {object[]} weaponTests - Extracted weapon test entries
     * @param {number|null} lotTestingId - Existing LOT id, or null for new entries
     */
    async appendWeaponTests(weaponTests, lotTestingId) {
        for (const test of weaponTests) {
            if (lotTestingId) {
                await this.addWeaponTest(lotTestingId, test);
            } else {
                this.addWeaponTestToNew(test);
            }
        }
    }
}

const store = new LotTestingStore();
export default store;
